import { Add20Filled } from '@fluentui/react-icons';
import { asUTC, Holiday } from '@mnspro-cloud/services';
import { isEqual, orderBy } from 'lodash';
import moment from 'moment-timezone';
import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../Button';
import { Flex } from '../../../Flex';
import { EditHolidayComponent } from './EditHolidayComponent/EditHolidayComponent';
import { HolidayWithEditComponent } from './HolidayWithEdit/HolidayWithEditComponent';

export interface HolidaysSettingsProps {
  holidays: Holiday[];
  saved?: (updatedHolidays: Holiday[]) => void;
}

export const HolidaysSettings: FC<HolidaysSettingsProps> = (props) => {
  const { holidays, saved } = props;
  const { t } = useTranslation();

  const [isCreateMode, setIsCreateMode] = useState<boolean>(false);
  const [holidayViewmodels, setHolidayViewModels] = useState<Holiday[]>(orderBy(holidays, 'start'));

  const handleAddButtonClicked = () => setIsCreateMode(true);

  const handleHolidayCancelButtonClicked = () => setIsCreateMode(false);

  const handleUpdateClicked = (oldHoliday: Holiday, newHoliday: Holiday) => {
    const updatedList = holidayViewmodels.map((holiday) =>
      isEqual(holiday, oldHoliday) ? newHoliday : holiday,
    );
    setHolidayViewModels(updatedList);
  };

  const handleDeleteConfirmed = (holidayToDelete: Holiday) => {
    const updatedList = holidayViewmodels.filter((holiday) => !isEqual(holiday, holidayToDelete));
    setHolidayViewModels(updatedList);
  };

  const handleAddHoliday = (holiday: Holiday) => {
    const updatedHolidayList = orderBy([...holidayViewmodels, holiday], 'start');
    setHolidayViewModels(updatedHolidayList);
    setIsCreateMode(false);
  };

  const handleSave = () => {
    saved?.(holidayViewmodels);
  };

  const handleCancel = () => {
    setHolidayViewModels(orderBy(holidays, 'start'));
  };

  const hasChanges = useMemo(() => {
    return !isEqual(orderBy(holidays, 'start'), holidayViewmodels);
  }, [holidays, holidayViewmodels]);

  return (
    <Flex direction="column" gap="0.5rem" data-testid="holidaysSettings">
      <Button
        appearance="primary"
        icon={<Add20Filled />}
        disabled={isCreateMode}
        onClick={handleAddButtonClicked}
      >
        {t('Add', { defaultValue: 'Hinzufügen' })}
      </Button>
      <Flex direction="column" gap="0.5rem">
        {isCreateMode && (
          <EditHolidayComponent
            name=""
            start={new Date().toString()}
            end={asUTC(moment(new Date()).endOf('D'))}
            cancelClicked={handleHolidayCancelButtonClicked}
            updateClicked={handleAddHoliday}
          />
        )}
        <Flex direction="column" gap="0.5rem">
          {holidayViewmodels.map((holiday, index) => (
            <HolidayWithEditComponent
              key={index}
              holiday={holiday}
              updateClicked={handleUpdateClicked}
              deleteClicked={handleDeleteConfirmed}
            />
          ))}
        </Flex>
      </Flex>
      <Flex gap="0.5rem" fill justify="flex-end">
        <Button onClick={handleCancel} disabled={!hasChanges}>
          {t('cancel', { defaultValue: 'Abbrechen' })}
        </Button>
        <Button appearance="primary" onClick={handleSave} disabled={!hasChanges}>
          {t('save', { defaultValue: 'Speichern' })}
        </Button>
      </Flex>
    </Flex>
  );
};
